import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Camera, Upload, Sparkles, RefreshCw, Scissors } from 'lucide-react';
import { toast } from 'sonner';

// ── Hairstyle knowledge base ────────────────────────────────
// Each style has a name and a photo.
// NOTE for owner: to use your OWN photos later, just replace the `img` value
// with your image link (or an uploaded file URL). Example:
//   { name: 'Pompadour', img: 'https://your-link.com/pompadour.jpg' }
//
// Right now we use free placeholder photos from Picsum (always load, no API key).
// Each style gets a stable, unique photo via a seed based on its name.
const IMG = (seed) => `https://picsum.photos/seed/${encodeURIComponent(seed.replace(/\s+/g, '-'))}/400/400`;

const FACE_SHAPES = {
  oval: {
    label: 'Oval',
    emoji: '🥚',
    desc: 'Balanced proportions — almost every style suits you.',
    men: [
      { name: 'Classic Side Part', img: IMG('men side part haircut') },
      { name: 'Textured Quiff', img: IMG('men quiff hairstyle') },
      { name: 'Crew Cut', img: IMG('men crew cut haircut') },
      { name: 'Slick Back', img: IMG('men slick back hair') },
      { name: 'Medium Fringe', img: IMG('men fringe haircut') },
    ],
    women: [
      { name: 'Long Layers', img: IMG('women long layered hair') },
      { name: 'Blunt Bob', img: IMG('women blunt bob haircut') },
      { name: 'Side-swept Bangs', img: IMG('women side bangs hairstyle') },
      { name: 'Beach Waves', img: IMG('women beach waves hair') },
      { name: 'High Ponytail', img: IMG('women high ponytail') },
    ],
  },
  round: {
    label: 'Round',
    emoji: '🌕',
    desc: 'Styles with height and angles add definition.',
    men: [
      { name: 'Pompadour', img: IMG('men pompadour hairstyle') },
      { name: 'High Fade with Volume', img: IMG('men high fade haircut') },
      { name: 'Faux Hawk', img: IMG('men faux hawk hairstyle') },
      { name: 'Angular Fringe', img: IMG('men angular fringe') },
      { name: 'Spiky Textured', img: IMG('men spiky hair') },
    ],
    women: [
      { name: 'Long Layered Cut', img: IMG('women long layers hair') },
      { name: 'Deep Side Part', img: IMG('women side part hair') },
      { name: 'Angled Long Bob', img: IMG('women long bob lob') },
      { name: 'High Updo', img: IMG('women updo hairstyle') },
      { name: 'Straight Long Hair', img: IMG('women straight long hair') },
    ],
  },
  square: {
    label: 'Square',
    emoji: '🟦',
    desc: 'Softer styles balance a strong jawline.',
    men: [
      { name: 'Textured Crop', img: IMG('men textured crop haircut') },
      { name: 'Side Part Fade', img: IMG('men side part fade') },
      { name: 'Buzz Cut', img: IMG('men buzz cut') },
      { name: 'Short Quiff', img: IMG('men short quiff') },
      { name: 'Ivy League', img: IMG('men ivy league haircut') },
    ],
    women: [
      { name: 'Soft Layers', img: IMG('women soft layers hair') },
      { name: 'Curled Lob', img: IMG('women curled lob hair') },
      { name: 'Side-swept Waves', img: IMG('women side swept waves') },
      { name: 'Wispy Bangs', img: IMG('women wispy bangs') },
      { name: 'Rounded Bob', img: IMG('women rounded bob') },
    ],
  },
  heart: {
    label: 'Heart',
    emoji: '💗',
    desc: 'Chin-length volume balances a wider forehead.',
    men: [
      { name: 'Medium Length Fringe', img: IMG('men medium fringe hair') },
      { name: 'Textured Side Part', img: IMG('men textured side part') },
      { name: 'Layered Mid-length', img: IMG('men medium length hair') },
      { name: 'Casual Sweep', img: IMG('men swept hair') },
      { name: 'Low Fade', img: IMG('men low fade haircut') },
    ],
    women: [
      { name: 'Chin-length Bob', img: IMG('women chin length bob') },
      { name: 'Side Bangs', img: IMG('women side bangs') },
      { name: 'Long Waves', img: IMG('women long wavy hair') },
      { name: 'Layered Lob', img: IMG('women layered lob') },
      { name: 'Curtain Bangs', img: IMG('women curtain bangs') },
    ],
  },
  long: {
    label: 'Long / Oblong',
    emoji: '📏',
    desc: 'Fuller sides and fringe add width and balance.',
    men: [
      { name: 'Side Fringe', img: IMG('men side fringe hair') },
      { name: 'Textured Crop', img: IMG('men textured crop') },
      { name: 'Medium Waves', img: IMG('men wavy hair') },
      { name: 'Caesar Cut', img: IMG('men caesar cut') },
      { name: 'Layered Medium', img: IMG('men layered medium hair') },
    ],
    women: [
      { name: 'Blunt Bangs', img: IMG('women blunt bangs') },
      { name: 'Shoulder Waves', img: IMG('women shoulder length waves') },
      { name: 'Voluminous Bob', img: IMG('women voluminous bob') },
      { name: 'Side Part Layers', img: IMG('women side part layers') },
      { name: 'Soft Curls', img: IMG('women soft curls hair') },
    ],
  },
};

const SHAPE_KEYS = Object.keys(FACE_SHAPES);

export default function StyleFinder() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [gender, setGender] = useState('men');       // men | women
  const [photo, setPhoto] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [shape, setShape] = useState(null);          // key of FACE_SHAPES
  const [manual, setManual] = useState(false);

  // Estimate face shape from an image using the browser FaceDetector when
  // available. It's not perfect — we always let the user correct it manually.
  const detectShape = async (imgDataUrl) => {
    setDetecting(true);
    try {
      const img = new Image();
      img.src = imgDataUrl;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });

      if ('FaceDetector' in window) {
        // eslint-disable-next-line no-undef
        const fd = new FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
        const faces = await fd.detect(img);
        if (faces && faces.length > 0) {
          const box = faces[0].boundingBox;
          const ratio = box.height / box.width; // taller vs wider
          let guess;
          if (ratio > 1.5) guess = 'long';
          else if (ratio > 1.35) guess = 'oval';
          else if (ratio > 1.2) guess = 'heart';
          else if (ratio > 1.05) guess = 'square';
          else guess = 'round';
          setShape(guess);
          setDetecting(false);
          return;
        }
      }
      // No detector or no face found → ask the user to pick
      setManual(true);
      setDetecting(false);
      toast.info('Apna face shape chunein', {
        description: 'Auto-detect is device pe available nahi — neeche se apna shape chunein.',
      });
    } catch (e) {
      setManual(true);
      setDetecting(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a photo');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhoto(reader.result);
      setShape(null);
      setManual(false);
      detectShape(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setPhoto('');
    setShape(null);
    setManual(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const current = shape ? FACE_SHAPES[shape] : null;
  const styles = current ? (gender === 'men' ? current.men : current.women) : [];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg,#fff5f2 0%,#ffffff 60%)' }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(8px)', borderBottom: '1px solid #ffe4e0',
        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/customer')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 20, color: '#111', margin: 0 }}>
            Style Finder
          </h1>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Apne face ke liye perfect hairstyle</p>
        </div>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px 60px' }}>
        {/* Gender toggle */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          {[['men', '💇‍♂️ Men'], ['women', '💇‍♀️ Women']].map(([g, label]) => (
            <button
              key={g}
              onClick={() => setGender(g)}
              style={{
                flex: 1, padding: '12px', borderRadius: 14, cursor: 'pointer',
                fontWeight: 700, fontSize: 14, transition: 'all .18s',
                border: gender === g ? '2px solid #dc2626' : '2px solid #eee',
                background: gender === g ? '#dc2626' : '#fff',
                color: gender === g ? '#fff' : '#555',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Photo area */}
        {!photo ? (
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed #f5a8a0', borderRadius: 20, padding: '40px 20px',
              textAlign: 'center', cursor: 'pointer', background: '#fff',
            }}
          >
            <div style={{
              width: 64, height: 64, borderRadius: '50%', margin: '0 auto 14px',
              background: 'linear-gradient(120deg,#e11d48,#7c2d12)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <Camera className="w-8 h-8 text-white" />
            </div>
            <p style={{ fontWeight: 700, color: '#111', margin: '0 0 4px' }}>Apni selfie lein ya photo upload karein</p>
            <p style={{ fontSize: 12, color: '#999', margin: 0 }}>Saaf, seedhi front photo best result deti hai</p>
            <div style={{ display: 'inline-flex', gap: 8, marginTop: 16 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dc2626', color: '#fff', padding: '10px 18px', borderRadius: 12, fontWeight: 700, fontSize: 14 }}>
                <Upload className="w-4 h-4" /> Choose Photo
              </span>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <img src={photo} alt="your face" style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 20, margin: '0 auto', display: 'block', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }} />
            <button onClick={reset} style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#dc2626', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              <RefreshCw className="w-4 h-4" /> Doosri photo
            </button>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} style={{ display: 'none' }} />

        {/* Detecting */}
        {detecting && (
          <div style={{ textAlign: 'center', marginTop: 24, color: '#dc2626', fontWeight: 600 }}>
            <Sparkles className="w-6 h-6 mx-auto mb-2 animate-pulse" />
            Face analyze ho raha hai...
          </div>
        )}

        {/* Manual pick */}
        {(manual && !shape) && (
          <div style={{ marginTop: 24 }}>
            <p style={{ fontWeight: 700, color: '#111', marginBottom: 10, textAlign: 'center' }}>Apna face shape chunein:</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {SHAPE_KEYS.map(k => (
                <button key={k} onClick={() => setShape(k)}
                  style={{ padding: '14px', borderRadius: 14, border: '2px solid #eee', background: '#fff', cursor: 'pointer', fontWeight: 700, color: '#444', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                  <span style={{ fontSize: 22 }}>{FACE_SHAPES[k].emoji}</span> {FACE_SHAPES[k].label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Result */}
        {current && (
          <div style={{ marginTop: 28 }}>
            <div style={{
              background: 'linear-gradient(120deg,#e11d48 0%,#7c2d12 100%)', borderRadius: 20,
              padding: '20px', color: '#fff', textAlign: 'center', marginBottom: 20,
            }}>
              <div style={{ fontSize: 40 }}>{current.emoji}</div>
              <p style={{ fontSize: 13, opacity: 0.9, margin: '4px 0 0' }}>Aapka face shape</p>
              <h2 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 26, margin: '2px 0 6px' }}>{current.label}</h2>
              <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>{current.desc}</p>
              <button onClick={() => { setShape(null); setManual(true); }} style={{ marginTop: 10, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 10, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                Shape sahi nahi? Badlein
              </button>
            </div>

            <h3 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: '#111', marginBottom: 12 }}>
              {gender === 'men' ? 'Men' : 'Women'} ke liye best hairstyles
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {styles.map((s, i) => (
                <div key={i} style={{
                  background: '#fff', border: '1px solid #f0e0dd', borderRadius: 16,
                  overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ width: '100%', aspectRatio: '1/1', background: '#f7f2f1', position: 'relative' }}>
                    <img
                      src={s.img}
                      alt={s.name}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        // If a photo fails to load, show a scissors placeholder
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement.innerHTML =
                          '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#fff0ee;color:#dc2626;font-size:30px">✂️</div>';
                      }}
                    />
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <span style={{ fontWeight: 700, color: '#222', fontSize: 13 }}>{s.name}</span>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={() => navigate('/customer')}
              className="w-full mt-6"
              style={{ background: '#dc2626', borderRadius: 14, padding: '14px', fontWeight: 700 }}
            >
              In styles ke liye salon book karein →
            </Button>

            <p style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
              Note: Yeh ek AI-based suggestion hai, approx result. Apne stylist se salah zaroor lein.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
